import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Area } from 'react-easy-crop';
import type { User } from '@supabase/supabase-js';
import { PageHeader } from '../ui/PageHeader';
import { supabase } from '../lib/supabase';
import { PhotoOrderPanel } from './VenueOutreachScreen';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { Skeleton } from '../ui/Skeleton';
import { useToast } from '../lib/useToast';
import { useEscToClose } from '../lib/useEscToClose';
import { VenuePhotoCropModal } from '../components/VenuePhotoCropModal';
import {
  buildSquareRecropPath,
  cropImageToJpegBlob,
  resolveVenuePhotoCropUrl,
  uploadVenuePhotoBlob,
} from '../lib/cropImage';
import {
  buildRestorePlan,
  loadPhotoWorkspace,
  type LivePhotoItem,
  type MediaEntry,
  type PendingPhotoItem,
  type PhotoWorkspace,
  type RestoreTier,
} from '../lib/venuePhotoOriginals';
import {
  approveVenuePhoto,
  rejectVenuePhoto,
  restoreOriginals,
  undoApproveVenuePhoto,
  undoRejectVenuePhoto,
  undoRestoreOriginals,
  type ApproveSnapshot,
  type RestoreOutcome,
  type RestoreWrite,
} from '../lib/venuePhotoApproval';

/** Error beacon (2026-09-22): Evan reports approve/reject "not working" while every server-side
 * path verifies healthy under his JWT — so the failure only exists in his browser. Each failure
 * on this screen reports itself to admin_client_errors, which sessions read via CLI. */
function beacon(page: string, message: string, detail?: Record<string, unknown>): void {
  try {
    /* ⚠️ supabase-js builders are lazy thenables — `void builder` NEVER SENDS THE REQUEST.
       Two days of "the log is empty so his tab must be stale" was built on that mistake. */
    supabase
      .from('admin_client_errors')
      .insert({ page, message, detail: detail ?? null })
      .then(
        () => {},
        () => {},
      );
  } catch {
    /* never let the beacon break the page */
  }
}
function errInfo(e: unknown): { message: string; detail: Record<string, unknown> } {
  const anyE = e as { message?: string; code?: string; details?: string; hint?: string; status?: number; name?: string; stack?: string } | null;
  return {
    message: (anyE?.message ?? String(e)).slice(0, 500),
    detail: {
      code: anyE?.code ?? null,
      details: anyE?.details ?? null,
      hint: anyE?.hint ?? null,
      status: anyE?.status ?? null,
      name: anyE?.name ?? null,
      stack: typeof anyE?.stack === 'string' ? anyE.stack.split('\n').slice(0, 4).join(' | ') : null,
      ua: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 120) : null,
      origin: typeof window !== 'undefined' ? window.location.origin : null,
    },
  };
}



/**
 * Carousels (Evan 2026-09-14): venues filtered by "at least N approved photos", each expandable to
 * the SAME PhotoOrderPanel the Venue Relations drawer uses — numbered thumbnails, ‹ › to reorder,
 * one Save that writes the carousel order back.
 */
type CarouselRow = { venue_id: string; venue_name: string | null; city_name: string | null; photo_count: number };

function CarouselManager({
  showSuccess,
  showError,
}: {
  showSuccess: (m: string) => void;
  showError: (m: string) => void;
}) {
  const [minPhotos, setMinPhotos] = useState('2');
  const [rows, setRows] = useState<CarouselRow[] | null>(null);
  const [openVenue, setOpenVenue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (min: number) => {
    const { data, error } = await supabase.rpc('admin_list_photo_carousels', {
      p_min_photos: min,
      p_limit: 300,
    });
    if (error) {
      showError(error.message);
      return;
    }
    setRows(Array.isArray(data) ? (data as CarouselRow[]) : []);
  }, [showError]);

  useEffect(() => {
    const min = Math.max(1, parseInt(minPhotos, 10) || 1);
    const t = window.setTimeout(() => void load(min), 250);
    return () => window.clearTimeout(t);
  }, [minPhotos, load]);

  const run = useCallback(
    async <T,>(fn: () => Promise<T>, success: string) => {
      setBusy(true);
      try {
        await fn();
        showSuccess(success);
      } catch (e) {
        showError(e instanceof Error ? e.message : 'Failed');
      } finally {
        setBusy(false);
      }
    },
    [showSuccess, showError],
  );

  return (
    <div className="mt-8">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h2 className="admin-eyebrow !mb-0">Carousels</h2>
        <label className="flex items-center gap-2 text-xs text-muted">
          venues with at least
          <input
            type="number"
            min={1}
            step={1}
            value={minPhotos}
            onChange={(e) => setMinPhotos(e.target.value)}
            className="admin-input !w-16 !px-2 !text-xs"
          />
          photos
        </label>
        {rows ? <span className="text-[11px] text-mutedDim">{rows.length} venues</span> : null}
      </div>
      {rows === null ? (
        <p className="text-[11px] text-mutedDim">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          compact
          title="No venues at that threshold"
          hint="Lower the photo count — only venues with that many APPROVED photos list here."
        />
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.venue_id} className="admin-card !p-0">
              <button
                type="button"
                onClick={() => setOpenVenue(openVenue === r.venue_id ? null : r.venue_id)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left"
              >
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-white">
                  {r.venue_name ?? '—'}
                </span>
                <span className="shrink-0 text-[11px] text-mutedDim">{r.city_name ?? '—'}</span>
                <span className="shrink-0 rounded-full border border-accent/35 bg-accent/10 px-2 py-0.5 text-[11px] tabular-nums text-accent">
                  {r.photo_count} photos
                </span>
                <span className="shrink-0 text-[11px] text-mutedDim">{openVenue === r.venue_id ? '▴' : '▾'}</span>
              </button>
              {openVenue === r.venue_id ? (
                <div className="border-t border-border px-4 py-3">
                  <PhotoOrderPanel venueId={r.venue_id} busy={busy} run={run} />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Photo Approval — the venue-photo queue, plus the tool that puts already-approved photos back
 * on the rater's original upload.
 *
 * Replaces the short-lived Re-Crop tab (2026-08-01 → 2026-08-02). Re-Crop assumed every live hero
 * needed re-cutting by hand; measuring prod showed 242 of the 287 photos behind approved heroes
 * were ALREADY square when uploaded, so for those the fix is a pointer change, not a crop.
 * See `src/lib/venuePhotoOriginals.ts` for how an original is resolved and why some cannot be.
 *
 * The Venues tab (`src/pages/VenueReviewScreen.tsx`) is deliberately untouched — its approval flow
 * still works exactly as before. The write sequence here is a documented duplicate of it; see the
 * warning at the top of `src/lib/venuePhotoApproval.ts`.
 */

type Mode = 'pending' | 'live' | 'add';

/* ── Add photos directly (Evan 2026-10-24: "search a venue and just add photos from my camera
 * roll for that venue super easily") ─────────────────────────────────────────────────────────
 * The file goes to the same admin-approved/<venue>/ Storage prefix the crop flow already writes
 * (so the dashboard account is known to have upload rights there); the row is then created
 * ALREADY APPROVED by the admin_add_venue_photo RPC — a direct client INSERT would hit the
 * restrictive can_write() policy, which a dashboard account may not satisfy. Every pick is
 * re-encoded to JPEG on a canvas first: camera-roll picks can be HEIC, which iOS Safari decodes
 * in an <img> but nothing downstream is guaranteed to render from Storage. */

/** Mirrors VenueReviewScreen's local helper — that file is frozen, so no shared export exists. */
function escapeForIlike(term: string): string {
  return term.replace(/[\\%_,]/g, (m) => `\\${m}`);
}

const ADD_PHOTO_MAX_DIM = 2048;

async function fileToJpegBlob(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('could not decode image'));
      el.src = url;
    });
    const scale = Math.min(1, ADD_PHOTO_MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight, 1));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas unavailable');
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) throw new Error('could not encode JPEG');
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

type AddVenueHit = { id: string; name: string; city: string; logs: number };
type StagedPhoto = { key: string; blob: Blob; previewUrl: string; fileName: string };

function AddPhotosPanel({
  showSuccess,
  showError,
}: {
  showSuccess: (m: string) => void;
  showError: (m: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<AddVenueHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [venue, setVenue] = useState<AddVenueHit | null>(null);
  const [venueInfo, setVenueInfo] = useState<{ approvedCount: number; hasHero: boolean } | null>(null);
  const [staged, setStaged] = useState<StagedPhoto[]>([]);
  const [reading, setReading] = useState(false);
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const raw = search.trim();
    if (venue || raw.length < 2) {
      setHits(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    setSearchError(null);
    const t = window.setTimeout(() => {
      const pattern = `%${escapeForIlike(raw)}%`;
      /* Ranked, not alphabetical: an A–Z order over 3.3M venues floats "@…"-named junk to the
       * top and buries the venue Evan actually means past the limit (2026-10-25: "search is not
       * working"). Most-logged + most-popular first is the venue he means almost every time;
       * the client then boosts name prefix matches above substring matches. */
      void supabase
        .from('venues')
        .select('id, venue_name_user_input, city_country, fsq_address, venue_shado_rating_count')
        .or(`venue_name_user_input.ilike.${pattern},city_country.ilike.${pattern},fsq_address.ilike.${pattern}`)
        .order('venue_shado_rating_count', { ascending: false, nullsFirst: false })
        .order('fsq_popularity', { ascending: false, nullsFirst: false })
        .limit(40)
        .then(
          ({ data, error }) => {
            if (cancelled) return;
            setSearching(false);
            if (error) {
              beacon('photo-approval/add-search', error.message);
              setSearchError(error.message);
              setHits(null);
              return;
            }
            const lower = raw.toLowerCase();
            const rows = ((data ?? []) as {
              id: string;
              venue_name_user_input: string | null;
              city_country: string | null;
              fsq_address: string | null;
              venue_shado_rating_count: number | null;
            }[]).map((r) => ({
              id: r.id,
              name: r.venue_name_user_input ?? 'Unnamed venue',
              city: r.city_country ?? r.fsq_address ?? '',
              logs: r.venue_shado_rating_count ?? 0,
            }));
            const score = (h: AddVenueHit) =>
              h.name.toLowerCase().startsWith(lower) ? 0 : h.name.toLowerCase().includes(lower) ? 1 : 2;
            rows.sort((a, b) => score(a) - score(b) || b.logs - a.logs);
            setHits(rows.slice(0, 25));
          },
          (e: unknown) => {
            if (cancelled) return;
            setSearching(false);
            const msg = e instanceof Error ? e.message : String(e);
            beacon('photo-approval/add-search', msg);
            setSearchError(msg);
            setHits(null);
          },
        );
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [search, venue]);

  useEffect(() => {
    if (!venue) {
      setVenueInfo(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const [photoRes, venueRes] = await Promise.all([
        supabase.from('venue_photos').select('id', { count: 'exact', head: true }).eq('venue_id', venue.id).eq('approved', true),
        supabase.from('venues').select('approved_venue_photo_url').eq('id', venue.id).maybeSingle(),
      ]);
      if (cancelled) return;
      setVenueInfo({
        approvedCount: photoRes.count ?? 0,
        hasHero: ((venueRes.data?.approved_venue_photo_url ?? null) as string | null) != null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [venue]);

  const addFiles = useCallback(
    async (list: FileList | null) => {
      if (!list || list.length === 0) return;
      setReading(true);
      const next: StagedPhoto[] = [];
      for (const file of Array.from(list)) {
        try {
          const blob = await fileToJpegBlob(file);
          next.push({
            key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            blob,
            previewUrl: URL.createObjectURL(blob),
            fileName: file.name,
          });
        } catch (e: unknown) {
          const info = errInfo(e);
          beacon('photo-approval/add-read', info.message, { file: file.name, type: file.type });
          showError(`Couldn't read ${file.name}`);
        }
      }
      setStaged((prev) => [...prev, ...next]);
      setReading(false);
      if (fileRef.current) fileRef.current.value = '';
    },
    [showError],
  );

  const removeStaged = useCallback((key: string) => {
    setStaged((prev) => {
      const gone = prev.find((p) => p.key === key);
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((p) => p.key !== key);
    });
  }, []);

  const upload = useCallback(async () => {
    if (!venue || staged.length === 0 || uploading) return;
    setUploading({ done: 0, total: staged.length });
    let heroSet = false;
    let ok = 0;
    const remaining = [...staged];
    try {
      for (let i = 0; i < staged.length; i += 1) {
        const p = staged[i];
        const { publicUrl, storagePath } = await uploadVenuePhotoBlob(
          `admin-approved/${venue.id}/upload-${Date.now()}-${i}.jpg`,
          p.blob,
        );
        const { data, error } = await supabase.rpc('admin_add_venue_photo', {
          p_venue_id: venue.id,
          p_url: publicUrl,
          p_storage_path: storagePath,
        });
        if (error) throw error;
        if ((data as { became_hero?: boolean } | null)?.became_hero) heroSet = true;
        ok += 1;
        URL.revokeObjectURL(p.previewUrl);
        remaining.shift();
        setUploading({ done: ok, total: staged.length });
      }
      setStaged([]);
      setVenueInfo((prev) =>
        prev ? { approvedCount: prev.approvedCount + ok, hasHero: prev.hasHero || heroSet } : prev,
      );
      showSuccess(
        heroSet
          ? `${ok} photo${ok === 1 ? '' : 's'} added to “${venue.name}” — the first is now the venue hero`
          : `${ok} photo${ok === 1 ? '' : 's'} added to “${venue.name}”'s carousel`,
      );
    } catch (e: unknown) {
      const info = errInfo(e);
      beacon('photo-approval/add-upload', info.message, info.detail);
      setStaged(remaining);
      showError(`Upload failed after ${ok} photo${ok === 1 ? '' : 's'}: ${info.message}`);
    } finally {
      setUploading(null);
    }
  }, [venue, staged, uploading, showSuccess, showError]);

  return (
    <div className="mx-auto w-full max-w-xl space-y-4">
      {/* Step 1 — pick the venue */}
      {venue ? (
        <div className="admin-card flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14px] font-medium text-textPrimary">{venue.name}</div>
            <div className="truncate text-[11px] text-mutedDim">
              {venue.city}
              {venueInfo
                ? ` · ${venueInfo.approvedCount} approved photo${venueInfo.approvedCount === 1 ? '' : 's'}${
                    venueInfo.hasHero ? '' : ' · no hero yet — your first photo becomes it'
                  }`
                : ''}
            </div>
          </div>
          <button
            type="button"
            className="admin-btn admin-btn-secondary admin-btn-sm shrink-0"
            disabled={uploading != null}
            onClick={() => {
              setVenue(null);
              setSearch('');
            }}
          >
            Change venue
          </button>
        </div>
      ) : (
        <div>
          <input
            type="search"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search a venue by name, city or address…"
            className="admin-input w-full !py-3 !text-[15px]"
          />
          {searching ? <p className="mt-2 text-[11px] text-mutedDim">Searching…</p> : null}
          {searchError && !searching ? (
            <p className="mt-2 text-[11px] text-red-400">
              Search failed: {searchError} — screenshot this line for Claude.
            </p>
          ) : null}
          {hits && hits.length === 0 && !searching ? (
            <p className="mt-2 text-[11px] text-mutedDim">No venue matches that.</p>
          ) : null}
          {hits && hits.length > 0 ? (
            <div className="mt-2 max-h-80 space-y-1 overflow-y-auto">
              {hits.map((h) => (
                <button
                  key={h.id}
                  type="button"
                  onClick={() => setVenue(h)}
                  className="admin-card admin-card-hover flex w-full items-center gap-3 !py-2.5 text-left"
                >
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-textPrimary">{h.name}</span>
                  {h.logs > 0 ? (
                    <span className="shrink-0 rounded-full bg-surfaceElevated px-2 py-0.5 text-[10px] text-accent">
                      {h.logs} log{h.logs === 1 ? '' : 's'}
                    </span>
                  ) : null}
                  <span className="max-w-[45%] shrink-0 truncate text-[11px] text-mutedDim">{h.city}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* Step 2 — pick photos */}
      {venue ? (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => void addFiles(e.target.files)}
          />
          <button
            type="button"
            className="admin-btn admin-btn-secondary w-full !py-4 !text-[15px]"
            disabled={reading || uploading != null}
            onClick={() => fileRef.current?.click()}
          >
            {reading ? 'Reading photos…' : staged.length > 0 ? 'Add more photos' : 'Pick photos from your camera roll'}
          </button>

          {staged.length > 0 ? (
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))' }}>
              {staged.map((p) => (
                <div key={p.key} className="relative">
                  <div className="aspect-square w-full overflow-hidden rounded-lg border border-border bg-surfaceElevated">
                    <img src={p.previewUrl} alt="" className="h-full w-full object-cover" />
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${p.fileName}`}
                    disabled={uploading != null}
                    onClick={() => removeStaged(p.key)}
                    className="absolute right-1 top-1 rounded-full bg-black/70 px-2 py-0.5 text-xs text-white"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {/* Step 3 — upload */}
          <button
            type="button"
            className="admin-btn admin-btn-primary w-full !py-4 !text-[15px]"
            disabled={staged.length === 0 || uploading != null || reading}
            onClick={() => void upload()}
          >
            {uploading
              ? `Uploading ${uploading.done} / ${uploading.total}…`
              : staged.length === 0
                ? 'Add photos'
                : `Add ${staged.length} photo${staged.length === 1 ? '' : 's'} to ${venue.name}`}
          </button>
          <p className="text-center text-[11px] leading-relaxed text-mutedDim">
            Photos go live immediately — they land at the end of the approved carousel
            {venueInfo && !venueInfo.hasHero ? ', and the first becomes the venue hero' : ''}. The app shows them
            square (centre-cut) — use the Pending tab&rsquo;s Crop if a photo needs framing.
          </p>
        </>
      ) : null}
    </div>
  );
}

type LiveFilterKey = 'restorable' | 'crop' | 'pick' | 'none' | 'original' | 'all';

const LIVE_FILTERS: { key: LiveFilterKey; label: string; title: string }[] = [
  {
    key: 'restorable',
    label: 'Ready to restore',
    title: "The rater's original is identified and already square — a one-click pointer change",
  },
  {
    key: 'crop',
    label: 'Needs a crop',
    title: 'Original found, but it is not square — someone has to choose the framing',
  },
  {
    key: 'pick',
    label: 'Pick the original',
    title: 'The rating had several photos and the link to this one was lost — choose which it was',
  },
  { key: 'none', label: 'No original', title: 'No rating behind this photo — nothing to restore' },
  { key: 'original', label: 'Already original', title: 'Still pointing at the untouched upload' },
  { key: 'all', label: 'All live', title: 'Every approved photo' },
];

function liveFilterOf(item: LivePhotoItem): Exclude<LiveFilterKey, 'all'> {
  if (item.confidence === 'alreadyOriginal') return 'original';
  if (item.confidence === 'none') return 'none';
  if (item.confidence === 'ambiguous') return 'pick';
  return item.restorable ? 'restorable' : 'crop';
}

type UndoEntry =
  | { kind: 'approve'; label: string; snapshot: ApproveSnapshot; caveat: string | null }
  | { kind: 'reject'; label: string; photoId: string }
  | { kind: 'restore'; label: string; writes: RestoreWrite[] };

/** Columns currently rendered by a CSS grid — lets ↑/↓ move a whole row. */
function gridColumnCount(el: HTMLElement | null): number {
  if (!el) return 1;
  const cols = window.getComputedStyle(el).gridTemplateColumns;
  const n = cols.split(' ').filter(Boolean).length;
  return n > 0 ? n : 1;
}

function toggleTier(prev: RestoreTier[], tier: RestoreTier, on: boolean): RestoreTier[] {
  if (!on) return prev.filter((t) => t !== tier);
  return prev.includes(tier) ? prev : [...prev, tier];
}

function dims(w: number | null, h: number | null): string {
  if (w == null || h == null) return 'size unknown';
  return `${w}×${h}`;
}

export function PhotoApprovalScreen({ user }: { user: User }) {
  const { showSuccess, showError, toastNode } = useToast();

  const [ws, setWs] = useState<PhotoWorkspace>({ pending: [], live: [], profileById: new Map() });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>('pending');
  const [query, setQuery] = useState('');
  const [liveFilter, setLiveFilter] = useState<LiveFilterKey>('restorable');
  const [heroesOnly, setHeroesOnly] = useState(false);
  const [liveSort, setLiveSort] = useState<'az' | 'new' | 'old'>('az');

  const [cursor, setCursor] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [undo, setUndo] = useState<UndoEntry | null>(null);
  const [undoBusy, setUndoBusy] = useState(false);

  /** Live photo whose original picker is open. */
  const [pickerId, setPickerId] = useState<string | null>(null);

  /** What the crop modal is composing, and what happens on confirm. */
  const [cropTarget, setCropTarget] = useState<
    | { kind: 'approve'; item: PendingPhotoItem; sourceUrl: string }
    | { kind: 'recrop'; item: LivePhotoItem; sourceUrl: string }
    | null
  >(null);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [cropBusy, setCropBusy] = useState(false);

  /**
   * Bulk restore. BOTH tiers are on by default (2026-08-02).
   *
   * `set` used to be opt-out because it was unclear whether shuffling the carousel was acceptable.
   * Evan resolved it: "the photos are approved… I don't want to change the order or change which
   * ones are primary or not." The restore honours that literally — it only ever rewrites
   * `venue_photos.public_url`, so `display_order` is untouched and the row that IS the hero stays
   * the hero (its venue pointer is moved to follow it). Within a `set`, WHICH original lands on
   * which row can still differ from the pre-crop truth; that link was destroyed at approval time
   * and is unrecoverable, but every candidate comes from the same rating on the same venue.
   */
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [tiers, setTiers] = useState<RestoreTier[]>(['exact', 'set']);
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState<{ done: number; total: number } | null>(null);
  const [restoreResult, setRestoreResult] = useState<RestoreOutcome[] | null>(null);

  const gridRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setWs(await loadPhotoWorkspace());
    } catch (e: unknown) {
      setWs({ pending: [], live: [], profileById: new Map() });
      setLoadError(e instanceof Error ? e.message : 'Could not load venue photos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* 2026-09-22 debugging: Evan sees skeletons ON CLICKING approve — only a fresh mount shows
   * skeletons, so the page appears to be reloading itself. Log every mount with the browser's
   * own navigation type ('reload' = the page reloaded; 'navigate' = normal SPA entry) and catch
   * anything uncaught, so the next click tells us exactly what is happening in his browser. */
  useEffect(() => {
    try {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      beacon('photo-approval/mount', `navigation=${nav?.type ?? 'unknown'}`, {
        visibility: document.visibilityState,
        historyLen: window.history.length,
      });
    } catch {
      /* diagnostics never break the page */
    }
    const onErr = (e: ErrorEvent) => beacon('photo-approval/window-error', String(e.message).slice(0, 300), { file: e.filename, line: e.lineno });
    const onRej = (e: PromiseRejectionEvent) => beacon('photo-approval/unhandled-rejection', String((e.reason as { message?: string })?.message ?? e.reason).slice(0, 300));
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onRej);
    return () => {
      window.removeEventListener('error', onErr);
      window.removeEventListener('unhandledrejection', onRej);
    };
  }, []);

  /* ── Derived lists ─────────────────────────────────────────────────────── */

  const liveCounts = useMemo(() => {
    const base: Record<LiveFilterKey, number> = {
      restorable: 0,
      crop: 0,
      pick: 0,
      none: 0,
      original: 0,
      all: ws.live.length,
    };
    for (const item of ws.live) base[liveFilterOf(item)] += 1;
    return base;
  }, [ws.live]);

  const q = query.trim().toLowerCase();

  const visiblePending = useMemo(
    () =>
      ws.pending.filter(
        (p) => !q || p.name.toLowerCase().includes(q) || p.city.toLowerCase().includes(q),
      ),
    [ws.pending, q],
  );

  const visibleLive = useMemo(() => {
    const filtered = ws.live.filter((item) => {
      if (liveFilter !== 'all' && liveFilterOf(item) !== liveFilter) return false;
      if (heroesOnly && !item.isHero) return false;
      if (q && !item.name.toLowerCase().includes(q) && !item.city.toLowerCase().includes(q)) return false;
      return true;
    });
    /* 'az' keeps the loader's name order; rows without a stamp (approved before the column
       existed) sink to the end in both date sorts rather than masquerading as newest/oldest */
    if (liveSort === 'new') {
      filtered.sort((a, b) => (b.approvedAt ?? '').localeCompare(a.approvedAt ?? ''));
    } else if (liveSort === 'old') {
      filtered.sort((a, b) => (a.approvedAt ?? '\uffff').localeCompare(b.approvedAt ?? '\uffff'));
    }
    return filtered;
  }, [ws.live, liveFilter, heroesOnly, q, liveSort]);

  const visibleCount = mode === 'pending' ? visiblePending.length : visibleLive.length;

  useEffect(() => {
    setCursor((c) => (visibleCount === 0 ? 0 : Math.min(c, visibleCount - 1)));
  }, [visibleCount]);

  useEffect(() => {
    setCursor(0);
  }, [mode, liveFilter, query, heroesOnly]);

  const pickerItem = useMemo(
    () => (pickerId ? (ws.live.find((i) => i.id === pickerId) ?? null) : null),
    [pickerId, ws.live],
  );

  const restorePlan = useMemo(() => buildRestorePlan(ws.live, tiers), [ws.live, tiers]);
  const exactCount = useMemo(
    () => ws.live.filter((i) => i.restorable && i.confidence === 'exact').length,
    [ws.live],
  );
  const setCount = useMemo(() => ws.live.filter((i) => i.restorable && i.confidence === 'set').length, [ws.live]);

  const anyModalOpen = pickerItem != null || cropTarget != null || restoreOpen;

  /* ── Crop source resolution (signed URL when the bucket needs it) ───────── */

  useEffect(() => {
    const url = cropTarget?.sourceUrl;
    if (!url) {
      setCropImageUrl(null);
      return;
    }
    let cancelled = false;
    void resolveVenuePhotoCropUrl(url).then((resolved) => {
      if (!cancelled) setCropImageUrl(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [cropTarget?.sourceUrl]);

  /* ── Actions ───────────────────────────────────────────────────────────── */

  const dropPending = useCallback((photoId: string) => {
    setWs((prev) => ({ ...prev, pending: prev.pending.filter((p) => p.id !== photoId) }));
  }, []);

  const approveAsIs = useCallback(
    async (item: PendingPhotoItem, croppedUrl?: string) => {
      setBusyId(item.id);
      try {
        const { becamePrimary, snapshot } = await approveVenuePhoto(
          { id: item.id, venue_id: item.venueId, public_url: item.publicUrl },
          user.id,
          croppedUrl,
        );
        dropPending(item.id);
        setUndo({
          kind: 'approve',
          label: `Approved “${item.name}”`,
          snapshot,
          caveat: snapshot.deletedGoogleObjectPath
            ? 'Undo restores the database, but the Google thumbnail this replaced was deleted from Storage and cannot come back.'
            : null,
        });
        showSuccess(
          becamePrimary ? `Approved “${item.name}” — now the venue hero` : `Approved “${item.name}” — added to the carousel`,
        );
      } catch (e: unknown) {
        const info = errInfo(e);
        beacon('photo-approval/approve', info.message, info.detail);
        setActionError(`Approve failed: ${info.message}${info.detail.code ? ` (code ${String(info.detail.code)})` : ''}`);
        showError(e instanceof Error ? e.message : 'Approve failed');
      } finally {
        setBusyId(null);
      }
    },
    [dropPending, showError, showSuccess, user.id],
  );

  const reject = useCallback(
    async (item: PendingPhotoItem) => {
      setBusyId(item.id);
      try {
        await rejectVenuePhoto(item.id);
        dropPending(item.id);
        setUndo({ kind: 'reject', label: `Rejected “${item.name}”`, photoId: item.id });
        beacon('photo-approval/reject-ok', item.id);
        showSuccess(`Rejected “${item.name}”`);
      } catch (e: unknown) {
        const info = errInfo(e);
        beacon('photo-approval/reject', info.message, info.detail);
        setActionError(`Reject failed: ${info.message}${info.detail.code ? ` (code ${String(info.detail.code)})` : ''}`);
        showError(e instanceof Error ? e.message : 'Reject failed');
      } finally {
        setBusyId(null);
      }
    },
    [dropPending, showError, showSuccess],
  );

  const runUndo = useCallback(async () => {
    if (!undo) return;
    setUndoBusy(true);
    try {
      if (undo.kind === 'approve') await undoApproveVenuePhoto(undo.snapshot);
      else if (undo.kind === 'reject') await undoRejectVenuePhoto(undo.photoId);
      else await undoRestoreOriginals(undo.writes);
      setUndo(null);
      showSuccess('Undone');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Undo failed');
    } finally {
      setUndoBusy(false);
    }
  }, [undo, load, showError, showSuccess]);

  /** Restore ONE approved photo to a chosen original — no image processing, pure pointer change. */
  const restoreOne = useCallback(
    async (item: LivePhotoItem, entry: MediaEntry) => {
      setBusyId(item.id);
      try {
        const write: RestoreWrite = {
          photoId: item.id,
          venueId: item.venueId,
          targetUrl: entry.url,
          isHero: item.isHero,
          previousUrl: item.currentUrl,
        };
        const [outcome] = await restoreOriginals([write]);
        if (!outcome.ok) throw new Error(outcome.error ?? 'Restore failed');
        setPickerId(null);
        setUndo({ kind: 'restore', label: `Restored “${item.name}” to the original`, writes: [write] });
        showSuccess(`Restored “${item.name}” to the rater's original`);
        await load();
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Restore failed');
      } finally {
        setBusyId(null);
      }
    },
    [load, showError, showSuccess],
  );

  const confirmCrop = useCallback(
    async (pixelCrop: Area) => {
      if (!cropTarget || !cropImageUrl) return;
      setCropBusy(true);
      try {
        const blob = await cropImageToJpegBlob(cropImageUrl, pixelCrop);
        if (cropTarget.kind === 'approve') {
          const item = cropTarget.item;
          // Same object path the Venues tab uses, so a photo never has two competing crops.
          const { publicUrl } = await uploadVenuePhotoBlob(`admin-approved/${item.venueId}/${item.id}.jpg`, blob);
          setCropTarget(null);
          await approveAsIs(item, publicUrl);
        } else {
          const item = cropTarget.item;
          // A NEW path every time: overwriting leaves the CDN serving the stale image.
          const { publicUrl } = await uploadVenuePhotoBlob(buildSquareRecropPath(item.venueId, item.id), blob);
          const write: RestoreWrite = {
            photoId: item.id,
            venueId: item.venueId,
            targetUrl: publicUrl,
            isHero: item.isHero,
            previousUrl: item.currentUrl,
          };
          const [outcome] = await restoreOriginals([write]);
          if (!outcome.ok) throw new Error(outcome.error ?? 'Could not save the crop');
          setCropTarget(null);
          setPickerId(null);
          setUndo({ kind: 'restore', label: `Re-cropped “${item.name}”`, writes: [write] });
          showSuccess(`Re-cropped “${item.name}” to 1:1`);
          await load();
        }
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Crop failed');
      } finally {
        setCropBusy(false);
      }
    },
    [cropTarget, cropImageUrl, approveAsIs, load, showError, showSuccess],
  );

  const runBulkRestore = useCallback(async () => {
    if (restorePlan.length === 0) return;
    const writes: RestoreWrite[] = restorePlan.map((p) => ({
      photoId: p.item.id,
      venueId: p.item.venueId,
      targetUrl: p.targetUrl,
      isHero: p.item.isHero,
      previousUrl: p.item.currentUrl,
    }));
    setRestoreRunning(true);
    setRestoreProgress({ done: 0, total: writes.length });
    setRestoreResult(null);
    try {
      const outcomes = await restoreOriginals(writes, (done, total) => setRestoreProgress({ done, total }));
      setRestoreResult(outcomes);
      const okWrites = outcomes.filter((o) => o.ok).map((o) => o.write);
      if (okWrites.length > 0) {
        setUndo({ kind: 'restore', label: `Restored ${okWrites.length} originals`, writes: okWrites });
      }
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Bulk restore failed');
    } finally {
      setRestoreRunning(false);
    }
  }, [restorePlan, load, showError]);

  /* ── Keyboard flow (pending queue) ─────────────────────────────────────── */

  useEffect(() => {
    if (mode !== 'pending' || anyModalOpen || loading) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const list = visiblePending;
      const cols = gridColumnCount(gridRef.current);
      const move = (delta: number) => {
        e.preventDefault();
        setCursor((c) => Math.max(0, Math.min(list.length - 1, c + delta)));
      };
      switch (e.key) {
        case 'ArrowRight':
        case 'j':
          return move(1);
        case 'ArrowLeft':
        case 'k':
          return move(-1);
        case 'ArrowDown':
          return move(cols);
        case 'ArrowUp':
          return move(-cols);
        default:
          break;
      }

      /* Letter/Enter action shortcuts REMOVED (Evan 2026-09-22): 'a' approved three photos by
       * accident, and he suspects 'r' in the reject mystery. Arrows still navigate; every
       * destructive action is buttons-only now. */
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    mode,
    anyModalOpen,
    loading,
    visiblePending,
    cursor,
    busyId,
    undo,
    undoBusy,
    approveAsIs,
    reject,
    runUndo,
  ]);

  const closePicker = useCallback(() => setPickerId(null), []);
  useEscToClose(pickerItem != null && cropTarget == null, closePicker);
  const closeRestore = useCallback(() => {
    if (!restoreRunning) setRestoreOpen(false);
  }, [restoreRunning]);
  useEscToClose(restoreOpen && cropTarget == null, closeRestore);

  /* ── Render ────────────────────────────────────────────────────────────── */

  const submitter = (userId: string | null) => {
    if (!userId) return 'Unknown';
    return ws.profileById.get(userId)?.username ?? `${userId.slice(0, 8)}…`;
  };

  return (
    <div className="admin-page">
      <PageHeader
        title="Photo Approval"
        subtitle={
          <>
            Approve venue photos straight from the grid, and put already-approved photos back on the rater&rsquo;s
            original upload. {ws.pending.length} pending · {liveCounts.restorable} ready to restore ·{' '}
            {liveCounts.crop + liveCounts.pick + liveCounts.none} need a look.
          </>
        }
        actions={
          <>
            {mode === 'live' ? (
              <button
                type="button"
                className="admin-btn admin-btn-primary admin-btn-sm"
                disabled={loading || exactCount + setCount === 0}
                onClick={() => {
                  setRestoreResult(null);
                  setRestoreProgress(null);
                  setRestoreOpen(true);
                }}
              >
                Restore originals…
              </button>
            ) : null}
            <button
              type="button"
              className="admin-btn admin-btn-secondary admin-btn-sm"
              onClick={() => void load()}
              disabled={loading}
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </>
        }
      />

            {actionError ? (
        <div className="admin-banner-error mb-4 flex items-start justify-between gap-4">
          <span className="min-w-0 break-words">{actionError} — screenshot this line for Claude.</span>
          <button type="button" className="shrink-0 underline" onClick={() => setActionError(null)}>
            dismiss
          </button>
        </div>
      ) : null}
{loadError ? <ErrorBanner className="mb-4">{loadError}</ErrorBanner> : null}

      {/* Mode switch */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button type="button" className="admin-pill" data-active={mode === 'pending'} onClick={() => setMode('pending')}>
          Pending <span className="text-mutedDim">{ws.pending.length}</span>
        </button>
        <button type="button" className="admin-pill" data-active={mode === 'live'} onClick={() => setMode('live')}>
          Live photos <span className="text-mutedDim">{ws.live.length}</span>
        </button>
        <button type="button" className="admin-pill" data-active={mode === 'add'} onClick={() => setMode('add')}>
          Add photos
        </button>
        {mode !== 'add' ? (
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by venue or city…"
            className="admin-input ml-auto w-full sm:w-64"
          />
        ) : null}
      </div>

      {mode === 'live' ? (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {LIVE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              title={f.title}
              data-active={liveFilter === f.key}
              className="admin-pill"
              onClick={() => setLiveFilter(f.key)}
            >
              {f.label} <span className="text-mutedDim">{liveCounts[f.key]}</span>
            </button>
          ))}
          <select
            value={liveSort}
            onChange={(e) => setLiveSort(e.target.value as 'az' | 'new' | 'old')}
            className="admin-input ml-auto h-7 w-auto py-0 text-[12px]"
            aria-label="Sort live photos"
          >
            <option value="az">A\u2013Z</option>
            <option value="new">Newest approved</option>
            <option value="old">Oldest approved</option>
          </select>
          <label className="flex cursor-pointer items-center gap-2 text-[12px] text-muted">
            <input
              type="checkbox"
              checked={heroesOnly}
              onChange={(e) => setHeroesOnly(e.target.checked)}
              className="accent-accent"
            />
            Heroes only
          </label>
        </div>
      ) : mode === 'pending' ? (
        <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-mutedDim">
          <span className="admin-eyebrow">Keyboard</span>
          <span>
            <kbd className="admin-chip-id">←</kbd> <kbd className="admin-chip-id">→</kbd> move
          </span>
          <span>approve / reject are buttons only — letter shortcuts removed so a stray keystroke can never act on a photo</span>
        </div>
      ) : null}

      {mode === 'add' ? (
        <AddPhotosPanel showSuccess={showSuccess} showError={showError} />
      ) : loading ? (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="aspect-square w-full rounded-lg" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      ) : mode === 'pending' ? (
        visiblePending.length === 0 ? (
          <EmptyState
            title="Queue is clear"
            hint={
              query
                ? 'No pending photo matches that search — clear it to see the rest.'
                : 'Every submitted venue photo has been approved or rejected.'
            }
          />
        ) : (
          <div ref={gridRef} className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
            {visiblePending.map((item, idx) => {
              const active = idx === cursor;
              const busy = busyId === item.id;
              return (
                <div
                  key={item.id}
                  onMouseEnter={() => setCursor(idx)}
                  data-active={active}
                  className={`admin-card overflow-hidden !p-0 transition-shadow ${
                    active ? 'ring-2 ring-accent' : ''
                  }`}
                >
                  {/* Square frame + object-cover = exactly what the app will render. */}
                  <div className="relative aspect-square w-full overflow-hidden bg-surfaceElevated">
                    <img src={item.publicUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/70 to-transparent" />
                    <div className="absolute left-2 top-2 flex flex-wrap gap-1">
                      <span
                        className={`admin-badge ${item.shape === 'square' ? 'admin-badge-accent' : 'admin-badge-warning'}`}
                        title={`Uploaded at ${dims(item.width, item.height)}`}
                      >
                        {item.shape === 'square' ? '1:1' : item.shape === 'unknown' ? '?' : 'not square'}
                      </span>
                      <span className="admin-badge admin-badge-neutral">
                        {item.venueHasPrimary ? `carousel #${item.liveCount + 1}` : 'becomes hero'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 px-3 py-2.5">
                    <div>
                      <div className="truncate text-[13px] font-medium text-textPrimary" title={item.name}>
                        {item.name}
                      </div>
                      <div className="truncate text-[11px] text-mutedDim">
                        {item.city} · {submitter(item.userId)}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void approveAsIs(item)}
                        className="admin-btn admin-btn-primary admin-btn-sm flex-1"
                        title="Approve the photo exactly as uploaded"
                      >
                        {busy ? '…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setCropTarget({ kind: 'approve', item, sourceUrl: item.publicUrl })}
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        title="Compose a 1:1 crop first"
                      >
                        Crop
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void reject(item)}
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        title="Reject"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : visibleLive.length === 0 ? (
        <EmptyState
          title="Nothing here"
          hint={
            query
              ? 'No live photo matches that search — clear it to see the rest.'
              : 'No approved photo falls into this group.'
          }
        />
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
          {visibleLive.map((item) => {
            const group = liveFilterOf(item);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setPickerId(item.id)}
                title={`${item.name} — ${item.city}`}
                className="admin-card admin-card-hover group overflow-hidden !p-0 text-left"
              >
                <div className="relative aspect-square w-full overflow-hidden bg-surfaceElevated">
                  <img
                    src={item.currentUrl}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                  />
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/70 to-transparent" />
                  <div className="absolute left-2 top-2 flex flex-wrap gap-1">
                    {item.isHero ? <span className="admin-badge admin-badge-accent">hero</span> : null}
                    {group === 'restorable' ? (
                      <span className="admin-badge admin-badge-accent" title="Original identified and square">
                        restorable
                      </span>
                    ) : group === 'crop' ? (
                      <span className="admin-badge admin-badge-warning" title="Original found but not square">
                        needs crop
                      </span>
                    ) : group === 'pick' ? (
                      <span className="admin-badge admin-badge-warning" title={`${item.candidates.length} possible originals`}>
                        pick 1 of {item.candidates.length}
                      </span>
                    ) : group === 'none' ? (
                      <span className="admin-badge admin-badge-danger" title="No rating behind this photo">
                        no original
                      </span>
                    ) : (
                      <span className="admin-badge admin-badge-neutral">original</span>
                    )}
                    {item.confidence === 'set' ? (
                      <span
                        className="admin-badge admin-badge-neutral"
                        title="Which photo of this rating maps to which row was lost — the set is right, the order may shuffle"
                      >
                        set
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="px-3 py-2.5">
                  <div className="truncate text-[13px] font-medium text-textPrimary">{item.name}</div>
                  <div className="truncate text-[11px] text-mutedDim">{item.city}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Original picker ─────────────────────────────────────────────── */}
      {pickerItem ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" role="presentation" onClick={closePicker}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="picker-title"
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 border-b border-border px-5 py-4">
              <h2 id="picker-title" className="font-display text-lg font-semibold tracking-tight text-white">
                {pickerItem.name}
              </h2>
              <p className="mt-1.5 text-xs text-mutedDim">
                {pickerItem.city} · {pickerItem.isHero ? 'venue hero' : `carousel position ${(pickerItem.displayOrder ?? 0) + 1}`}
              </p>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
              {pickerItem.confidence === 'none' ? (
                <ErrorBanner>
                  No original could be resolved — this photo row has no <code>log_id</code>, so there is no rating to
                  recover it from. {pickerItem.source === 'venue_upload' ? 'It was a direct venue upload, and the source file was deleted when it was approved. ' : ''}
                  The only option is to re-crop the already-cropped live file, which loses quality and gains no framing headroom.
                </ErrorBanner>
              ) : pickerItem.confidence === 'ambiguous' ? (
                <ErrorBanner>
                  This rating carried several photos and the link saying which one became this approved photo was
                  erased at approval time. Any of the {pickerItem.candidates.length} below could be it — pick the one
                  the live crop was taken from.
                </ErrorBanner>
              ) : pickerItem.confidence === 'set' ? (
                <ErrorBanner>
                  Several photos from this one rating are live on this venue. The set of originals is certain, but which
                  original belongs to which row is not — restoring may swap their carousel order. Same venue, same
                  rating, so no foreign photo can appear.
                </ErrorBanner>
              ) : null}

              <div>
                <div className="admin-eyebrow mb-2">Live now</div>
                <div className="flex items-start gap-4">
                  <div className="aspect-square w-28 shrink-0 overflow-hidden rounded-lg border border-border bg-surfaceElevated">
                    <img src={pickerItem.currentUrl} alt="" className="h-full w-full object-cover" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs leading-relaxed text-mutedDim">
                      What the app serves today. Restoring an original <strong>discards this framing</strong> — if the
                      crop was made to cut something out of frame, that comes back.
                    </p>
                    <button
                      type="button"
                      className="admin-btn admin-btn-ghost admin-btn-sm"
                      onClick={() => setCropTarget({ kind: 'recrop', item: pickerItem, sourceUrl: pickerItem.currentUrl })}
                    >
                      Re-crop the live file
                    </button>
                  </div>
                </div>
              </div>

              {pickerItem.candidates.length > 0 ? (
                <div>
                  <div className="admin-eyebrow mb-2">
                    {pickerItem.candidates.length === 1 ? 'Original from the rating' : `${pickerItem.candidates.length} possible originals`}
                  </div>
                  <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
                    {pickerItem.candidates.map((c) => {
                      const square = c.width != null && c.height != null && c.width === c.height;
                      const proposed = pickerItem.proposed?.url === c.url;
                      return (
                        <div
                          key={c.url}
                          className={`admin-card overflow-hidden !p-0 ${proposed ? 'ring-1 ring-accent' : ''}`}
                        >
                          <div className="aspect-square w-full overflow-hidden bg-surfaceElevated">
                            <img src={c.url} alt="" loading="lazy" className="h-full w-full object-contain" />
                          </div>
                          <div className="space-y-1.5 px-2.5 py-2">
                            <div className="flex items-center gap-1.5 text-[10px] text-mutedDim">
                              <span className={`admin-badge ${square ? 'admin-badge-accent' : 'admin-badge-warning'}`}>
                                {dims(c.width, c.height)}
                              </span>
                              {proposed ? <span className="admin-badge admin-badge-neutral">proposed</span> : null}
                            </div>
                            <div className="flex gap-1.5">
                              <button
                                type="button"
                                disabled={!square || busyId === pickerItem.id}
                                title={
                                  square
                                    ? 'Point this photo straight at the original — no image processing'
                                    : 'Not square: restoring it as-is would be centre-cut by the app. Crop it instead.'
                                }
                                onClick={() => void restoreOne(pickerItem, c)}
                                className="admin-btn admin-btn-primary admin-btn-sm flex-1"
                              >
                                Restore
                              </button>
                              <button
                                type="button"
                                disabled={busyId === pickerItem.id}
                                onClick={() => setCropTarget({ kind: 'recrop', item: pickerItem, sourceUrl: c.url })}
                                className="admin-btn admin-btn-secondary admin-btn-sm"
                              >
                                Crop
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex shrink-0 justify-end border-t border-border px-5 py-4">
              <button type="button" className="admin-btn admin-btn-secondary" onClick={closePicker}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Bulk restore ────────────────────────────────────────────────── */}
      {restoreOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" role="presentation" onClick={closeRestore}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="restore-title"
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 border-b border-border px-5 py-4">
              <h2 id="restore-title" className="font-display text-lg font-semibold tracking-tight text-white">
                Restore originals
              </h2>
              <p className="mt-1.5 text-xs text-mutedDim">
                Points approved photos back at the rater&rsquo;s untouched upload. No image processing — the file is
                already square, so this is a pointer change.
              </p>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
              {restoreResult ? (
                <div className="space-y-3">
                  <div className="admin-eyebrow">Result</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="admin-status admin-status-success">
                      {restoreResult.filter((r) => r.ok).length} restored
                    </span>
                    {restoreResult.some((r) => !r.ok) ? (
                      <span className="admin-status admin-status-danger">
                        {restoreResult.filter((r) => !r.ok).length} failed
                      </span>
                    ) : null}
                  </div>
                  {restoreResult.some((r) => !r.ok) ? (
                    <ul className="max-h-40 space-y-1 overflow-y-auto text-[11px] text-mutedDim">
                      {restoreResult
                        .filter((r) => !r.ok)
                        .map((r) => (
                          <li key={r.write.photoId}>
                            <code>{r.write.photoId.slice(0, 8)}</code> — {r.error}
                          </li>
                        ))}
                    </ul>
                  ) : null}
                  <p className="text-xs leading-relaxed text-mutedDim">
                    The cropped files were left in Storage on purpose, so this is fully reversible from the undo bar.
                  </p>
                </div>
              ) : (
                <>
                  <ErrorBanner>
                    This discards the framing chosen when each photo was approved. If a crop existed to cut something
                    out of frame — a face, a bad edge — that comes back.
                  </ErrorBanner>

                  <div className="space-y-2">
                    <div className="admin-eyebrow">What to include</div>
                    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3">
                      <input
                        type="checkbox"
                        className="mt-0.5 accent-accent"
                        checked={tiers.includes('exact')}
                        onChange={(e) => setTiers((prev) => toggleTier(prev, 'exact', e.target.checked))}
                      />
                      <span className="text-xs leading-relaxed text-muted">
                        <strong className="text-textPrimary">Exact match — {exactCount} photos.</strong> Exactly one
                        candidate original, and it is square. No judgement involved.
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3">
                      <input
                        type="checkbox"
                        className="mt-0.5 accent-accent"
                        checked={tiers.includes('set')}
                        onChange={(e) => setTiers((prev) => toggleTier(prev, 'set', e.target.checked))}
                      />
                      <span className="text-xs leading-relaxed text-muted">
                        <strong className="text-textPrimary">Same-rating set — {setCount} photos.</strong> Several
                        photos from one rating are live on the same venue and all their originals are square. Order and
                        hero position are kept; within the set, which original lands on which slot may differ from
                        before — same rating, same venue.
                      </span>
                    </label>
                  </div>

                  <div>
                    <div className="admin-eyebrow mb-2">
                      Preview — {restorePlan.length} photo{restorePlan.length === 1 ? '' : 's'} will change
                      {restorePlan.filter((p) => p.item.isHero).length > 0
                        ? `, ${restorePlan.filter((p) => p.item.isHero).length} of them the venue hero`
                        : ''}
                    </div>
                    {restorePlan.length === 0 ? (
                      <EmptyState compact title="Nothing selected" hint="Tick at least one group above." />
                    ) : (
                      <div className="grid max-h-64 gap-3 overflow-y-auto pr-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                        {restorePlan.slice(0, 60).map((p) => (
                          <div key={p.item.id} className="space-y-1">
                            <div className="grid grid-cols-2 gap-1">
                              <div className="aspect-square overflow-hidden rounded border border-border bg-surfaceElevated">
                                <img src={p.item.currentUrl} alt="" loading="lazy" className="h-full w-full object-cover opacity-50" />
                              </div>
                              <div className="aspect-square overflow-hidden rounded border border-accent/60 bg-surfaceElevated">
                                <img src={p.targetUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
                              </div>
                            </div>
                            <div className="truncate text-[10px] text-mutedDim" title={p.item.name}>
                              {p.item.isHero ? '★ ' : ''}
                              {p.item.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {restorePlan.length > 60 ? (
                      <p className="mt-2 text-[11px] text-mutedDim">Showing the first 60 of {restorePlan.length}.</p>
                    ) : null}
                  </div>

                  <div className="rounded-lg border border-border bg-surfaceElevated p-3 text-[11px] leading-relaxed text-mutedDim">
                    <div className="admin-eyebrow mb-1">Exactly what runs</div>
                    Per photo: <code>venue_photos.public_url</code> ← the original flash URL, then — for a hero only —{' '}
                    <code>admin_set_approved_venue_photo(venue, url)</code> so the venue keeps pointing at{' '}
                    <em>that same row</em>. <code>display_order</code> is never written and no row changes role. No
                    uploads, no deletes; the cropped file stays in Storage so this can be undone.
                  </div>
                </>
              )}
            </div>

            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-5 py-4">
              <span className="text-[11px] text-mutedDim">
                {restoreRunning && restoreProgress ? `Restoring ${restoreProgress.done} / ${restoreProgress.total}…` : ''}
              </span>
              <div className="flex gap-2">
                <button type="button" className="admin-btn admin-btn-secondary" disabled={restoreRunning} onClick={closeRestore}>
                  {restoreResult ? 'Done' : 'Cancel'}
                </button>
                {restoreResult ? null : (
                  <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    disabled={restoreRunning || restorePlan.length === 0}
                    onClick={() => void runBulkRestore()}
                  >
                    {restoreRunning ? 'Restoring…' : `Restore ${restorePlan.length} photo${restorePlan.length === 1 ? '' : 's'}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Crop ────────────────────────────────────────────────────────── */}
      {cropTarget && cropImageUrl ? (
        <VenuePhotoCropModal
          key={cropTarget.sourceUrl}
          imageUrl={cropImageUrl}
          title={
            cropTarget.kind === 'approve'
              ? `Crop & approve — ${cropTarget.item.name}`
              : `Re-crop to 1:1 — ${cropTarget.item.name}`
          }
          aspect={1}
          lockAspect
          confirmLabel={cropTarget.kind === 'approve' ? 'Approve crop' : 'Save square crop'}
          busyLabel="Saving…"
          busy={cropBusy}
          onCancel={() => {
            if (!cropBusy) setCropTarget(null);
          }}
          onConfirm={(pixelCrop) => void confirmCrop(pixelCrop)}
        />
      ) : cropTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 text-sm text-muted">Loading image…</div>
      ) : null}

      {/* ── Undo ────────────────────────────────────────────────────────── */}
      {undo ? (
        <div className="fixed bottom-6 left-6 z-[55] flex max-w-sm items-start gap-3 rounded-lg border border-border bg-surface px-4 py-2.5 shadow-lg shadow-black/40">
          <div className="min-w-0">
            <div className="truncate text-[12px] text-textPrimary">{undo.label}</div>
            {undo.kind === 'approve' && undo.caveat ? (
              <div className="mt-0.5 text-[10px] leading-snug text-mutedDim">{undo.caveat}</div>
            ) : null}
          </div>
          <button
            type="button"
            className="admin-btn admin-btn-secondary admin-btn-sm shrink-0"
            disabled={undoBusy}
            onClick={() => void runUndo()}
          >
            {undoBusy ? '…' : 'Undo'}
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            className="shrink-0 text-xs text-mutedDim hover:text-white"
            onClick={() => setUndo(null)}
          >
            ✕
          </button>
        </div>
      ) : null}

      {mode !== 'add' ? <CarouselManager showSuccess={showSuccess} showError={showError} /> : null}

      {toastNode}
    </div>
  );
}
